import { useEffect, useState } from 'react'
import { UserCog, UserPlus, ShieldCheck, User as UserIcon, CheckCircle2, AlertTriangle, Mail } from 'lucide-react'
import { supabase, isDemoMode } from '../../lib/supabase'
import { DemoNotice } from '../../lib/demo'

// List the staff + client users, plus invite new ones (staff or client).
export default function Team() {
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(false)
  const [inviting, setInviting] = useState(false)
  const [form, setForm] = useState({ email: '', full_name: '', role: 'staff' })
  const [lastResult, setLastResult] = useState(null)
  const [currentUserId, setCurrentUserId] = useState(null)

  useEffect(() => {
    if (isDemoMode) return
    supabase.auth.getUser().then(({ data }) => setCurrentUserId(data?.user?.id || null))
  }, [])

  const load = async () => {
    if (isDemoMode) return
    setLoading(true)
    const { data, error } = await supabase.rpc('get_team_members')
    if (error) alert(error.message)
    setMembers(data || [])
    setLoading(false)
  }
  useEffect(() => { load() }, [])

  const invite = async (e) => {
    e.preventDefault()
    if (isDemoMode) return
    if (!form.email.trim()) return
    setInviting(true)
    setLastResult(null)
    try {
      const { data, error } = await supabase.functions.invoke('invite-users', {
        body: {
          users: [{
            email: form.email.trim().toLowerCase(),
            full_name: form.full_name.trim() || form.email.trim().split('@')[0],
            role: form.role,
          }]
        }
      })
      if (error) throw error
      const r = data?.results?.[0]
      setLastResult(r)
      if (r?.ok) {
        setForm({ email: '', full_name: '', role: form.role })
        await load()
      }
    } catch (err) {
      setLastResult({ ok: false, error: err.message })
    } finally {
      setInviting(false)
    }
  }

  const promote = async (userId, newRole) => {
    if (isDemoMode) return
    // Safety: don't let staff lock themselves (or all staff) out by demotion.
    if (newRole === 'client') {
      if (userId === currentUserId) {
        return alert("You can't demote yourself. If you really want to step back, ask another staff user to do it.")
      }
      const staffCount = members.filter(m => m.role === 'staff').length
      if (staffCount <= 1) {
        return alert("There must always be at least one staff member. Invite another staff user first, then demote this one.")
      }
    }
    if (!confirm(`Change this user's role to ${newRole}?`)) return
    const { error } = await supabase.from('user_profiles').update({ role: newRole }).eq('user_id', userId)
    if (error) return alert(error.message)
    load()
  }

  const staff = members.filter(m => m.role === 'staff')
  const clients = members.filter(m => m.role === 'client')

  return (
    <div className="space-y-4 max-w-3xl">
      <DemoNotice />

      <div className="card">
        <h3 className="font-semibold text-gray-800 mb-1 flex items-center gap-2">
          <UserCog className="w-5 h-5 text-brand" />
          Team
        </h3>
        <p className="text-sm text-gray-500">
          Everyone with an account. Staff see the full admin app; clients only see their own sites' portal.
        </p>
      </div>

      <div className="card">
        <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <UserPlus className="w-5 h-5 text-brand" />
          Invite someone
        </h3>
        <form onSubmit={invite} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto_auto] gap-2 mb-3">
          <input
            type="text"
            value={form.full_name}
            onChange={e => setForm({ ...form, full_name: e.target.value })}
            placeholder="Full name (e.g. Jane Doe)"
            className="input-field"
          />
          <input
            type="email"
            value={form.email}
            onChange={e => setForm({ ...form, email: e.target.value })}
            placeholder="email@waltergeering.co.uk"
            className="input-field"
            required
          />
          <select
            value={form.role}
            onChange={e => setForm({ ...form, role: e.target.value })}
            className="input-field"
          >
            <option value="staff">Staff (admin)</option>
            <option value="client">Client</option>
          </select>
          <button type="submit" disabled={inviting} className="btn-primary">
            <Mail className="w-4 h-4" /> {inviting ? 'Sending…' : 'Send invite'}
          </button>
        </form>
        <p className="text-xs text-gray-500">
          They get a password-set email from Supabase. If you're inviting a <strong>client</strong>, you still need to link them to one or more sites — either edit the user once they've accepted or use <a href="/admin/import-clients" className="text-brand-dark underline">Import Clients</a> for bulk site-linking.
        </p>

        {lastResult && (
          <div className={`mt-3 p-3 rounded-lg text-sm flex items-start gap-2 ${lastResult.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-700'}`}>
            {lastResult.ok ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /> : <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />}
            <div>
              {lastResult.ok
                ? <>Invite {lastResult.invited ? 'sent' : 'updated existing user'} for <code>{lastResult.email}</code>{lastResult.role ? ` as ${lastResult.role}` : ''}.</>
                : <>Failed: {lastResult.error}</>
              }
            </div>
          </div>
        )}
      </div>

      <div className="card">
        <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-brand" />
          Staff ({staff.length})
        </h3>
        {loading && <p className="text-sm text-gray-500">Loading…</p>}
        {!loading && staff.length === 0 && <p className="text-sm text-gray-500">No staff users yet.</p>}
        <ul className="divide-y divide-gray-100">
          {staff.map(m => {
            const isMe = m.user_id === currentUserId
            const isLastStaff = staff.length <= 1
            const protectedRow = isMe || isLastStaff
            return (
              <li key={m.user_id} className="py-2 flex items-center gap-3">
                <ShieldCheck className="w-5 h-5 text-brand" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-800 truncate flex items-center gap-2">
                    {m.full_name || m.email}
                    {isMe && <span className="text-[10px] uppercase tracking-wide bg-brand-50 text-brand-dark px-1.5 py-0.5 rounded-full">You</span>}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {m.email}
                    {m.last_sign_in_at ? ` · last signed in ${new Date(m.last_sign_in_at).toLocaleDateString()}` : ' · never signed in'}
                  </div>
                </div>
                <button
                  onClick={() => promote(m.user_id, 'client')}
                  disabled={protectedRow}
                  className="text-xs text-gray-500 hover:text-red-600 disabled:text-gray-300 disabled:cursor-not-allowed"
                  title={isMe ? "You can't demote yourself" : isLastStaff ? 'At least one staff user is required' : 'Demote to client'}
                >
                  Demote to client
                </button>
              </li>
            )
          })}
        </ul>
      </div>

      <div className="card">
        <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <UserIcon className="w-5 h-5 text-brand" />
          Clients ({clients.length})
        </h3>
        {!loading && clients.length === 0 && <p className="text-sm text-gray-500">No client users yet.</p>}
        {clients.length > 0 && (
          <ul className="divide-y divide-gray-100 max-h-96 overflow-auto">
            {clients.map(m => (
              <li key={m.user_id} className="py-2 flex items-center gap-3">
                <UserIcon className="w-5 h-5 text-gray-400" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-800 truncate">{m.full_name || m.email}</div>
                  <div className="text-xs text-gray-500 truncate">
                    {m.email}
                    {m.last_sign_in_at ? ` · last signed in ${new Date(m.last_sign_in_at).toLocaleDateString()}` : ' · never signed in'}
                  </div>
                </div>
                <button onClick={() => promote(m.user_id, 'staff')} className="text-xs text-brand-dark hover:underline">
                  Promote to staff
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
